import type React from "react";
import type { SVGProps } from "react";

import {
  DollarSeal,
  FreeShippingTruck,
  GuaranteeCheckmark,
  GuaranteeRibbon,
} from "./value-prop-icons";

export interface TelehealthValueProp {
  Icon: (props: SVGProps<SVGSVGElement>) => React.ReactElement;
  label: string;
}

/**
 * The trust row under the Get Started CTA, copy from ET-509 (icons from XD-149
 * node 3298-17209). Supersedes ET-216's set ("…-eligible online care",
 * "Affordable FDA-approved options", …).
 *
 * Authored in title case; the row renders it upper-case with CSS, as the design
 * does, so assistive tech reads words rather than spelling out capitals.
 *
 * `siteLabel` is the site identifier the eligibility badges already derive
 * ("FSA" / "HSA" / "FSA/HSA" — `ELIGIBILITY_LABEL_BY_SITE` minus " Eligible").
 * The design is drawn on FSA, but this row ships on all three storefronts, so
 * the first prop cannot hardcode "FSA".
 */
export function telehealthValueProps(
  siteLabel: string | null,
): readonly TelehealthValueProp[] {
  return [
    // No label ⇒ drop the item. Substituting a default program would tell an
    // HSA or WDH shopper they're on FSA, which is worse than one fewer prop.
    ...(siteLabel
      ? [{ Icon: GuaranteeCheckmark, label: `100% ${siteLabel} Eligible` }]
      : []),
    { Icon: FreeShippingTruck, label: "Free, Discreet Shipping" },
    { Icon: GuaranteeRibbon, label: "Licensed Provider" },
    { Icon: DollarSeal, label: "No Membership/Consult Fee" },
  ];
}

/**
 * Intake attribution for a CTA in the treatment PDP's buy column. Shared by the
 * coded Get Started button and published through `TelehealthPdpPriceProvider`
 * so a Builder-authored CTA in the same slot reports identically — otherwise
 * authoring an entry silently reattributes that medication's intake starts
 * (ET-216).
 *
 * Re-exported from `~/hec/telehealth/cta-contexts` (which also holds the
 * below-the-fold section's context) so the two can't drift; kept exported here
 * for the existing PDP consumers.
 */
export { PDP_CTA_CONTEXT } from "~/hec/telehealth/cta-contexts";
